/**
 * Needs-you items from the timeline module: agents marked running that went quiet (stuck), and
 * pipeline work rejected more than once and not landed since (a rebase loop). Pure, so tested.
 */
import { pipelineKey } from '@orgus/protocol';
import type { Machine, Pipeline, Project, Session } from '@orgus/protocol';
import { formatDuration } from '@orgus/ui';
import { stuckRuns } from '../agents/activity.ts';
import { chainSummary, repeatedRejections, times } from '../pipelines/chains.ts';
import type { NeedItem } from './needsYou.ts';
import { shownTitle } from '../sessions/shownTitle.ts';

interface Where {
  machines: Record<string, Machine>;
  projects: Record<string, Project>;
}

const whereOf = (e: Where, machineId: string, projectId: string | null) =>
  [projectId ? (e.projects[projectId]?.name ?? null) : null, e.machines[machineId]?.name ?? null].filter(Boolean).join(' on ');

/** The Agents page window that shows a run started at `startedAt`. */
function windowFor(startedAt: number, now: number): string {
  const age = now - startedAt;
  return age <= 60 * 60_000 ? '1h' : age <= 6 * 60 * 60_000 ? '6h' : '24h';
}

/** Agents that look stuck: marked running, nothing in their transcript for a while. */
export function stuckAgentNeeds(e: Where & { sessions: Record<string, Session> }, now: number): NeedItem[] {
  return stuckRuns(Object.values(e.sessions), e.machines, now).map(({ run, session, quietMs, busy }) => {
    const win = windowFor(run.startedAt, now);
    return {
      key: `a:${run.sessionId}:${run.id}`,
      kind: 'agent',
      title: `${run.title} looks stuck`,
      detail: busy
        ? `One tool call or reply has been outstanding for ${formatDuration(quietMs)}. In ${shownTitle(session).title}`
        : `Marked running, but nothing happened for ${formatDuration(quietMs)}. In ${shownTitle(session).title}`,
      where: whereOf(e, session.machineId, session.projectId),
      at: now - quietMs,
      to: { path: `/agents?run=${encodeURIComponent(run.id)}${win === '1h' ? '' : `&window=${win}`}` },
      verb: 'Inspect',
      subject: { kind: 'session', id: run.sessionId },
    };
  });
}

/** Failed items a repeated-rejection row already covers (so they are not listed twice). */
export function repeatedIds(pipelines: Record<string, Pipeline>, now: number, windowMs: number): Set<string> {
  const ids = new Set<string>();
  for (const p of Object.values(pipelines)) for (const c of repeatedRejections(p, now, windowMs)) for (const a of c.attempts) ids.add(a.id);
  return ids;
}

/** Work rejected more than once and not landed since: one row per piece of work. */
export function repeatedRejectionNeeds(e: Where & { pipelines: Record<string, Pipeline> }, now: number, windowMs: number): NeedItem[] {
  const out: NeedItem[] = [];
  for (const p of Object.values(e.pipelines)) {
    const key = pipelineKey(p);
    for (const c of repeatedRejections(p, now, windowMs)) {
      const lastFail = [...c.attempts].reverse().find((a) => a.state === 'failed');
      out.push({
        key: `r:${key}:${c.key}`,
        kind: 'pipeline',
        title: `${c.title}: rejected ${times(c.rejected)}`,
        detail: [chainSummary(c, p), lastFail?.detail].filter(Boolean).join('. Last: '),
        where: whereOf(e, p.machineId, p.projectId),
        at: lastFail?.endedAt ?? c.lastAt,
        to: { path: `/pipelines?pipeline=${encodeURIComponent(key)}&item=${encodeURIComponent(c.latest.id)}` },
        verb: 'See history',
      });
    }
  }
  return out;
}
